import React, { useEffect, useState } from 'react';
import '../../css/ProjectImages.css';
import { useParams } from 'react-router-dom';
import ImagePreviewModal from '../../modals/ImagePreviewModal';
import { getImage, setImage, clearImageCache } from '../../utils/idbHelpers';
import { useProject } from "../../utils/ProjectContext";
import { 
    Button, 
    Checkbox, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogTitle, 
    CircularProgress, 
    LinearProgress,
    Typography,
    Grid,
    IconButton,
    Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';


const ProjectImages = () => {
    const {projectName, BASE_URL, projectData, setProjectData} = useProject();
    const { projectId } = useParams();
    const [images, setImages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [filter, setFilter] = useState('all');
    const token = localStorage.getItem('token');
    const [progress, setProgress] = useState(0);
    const [selectedImages, setSelectedImages] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteStatus, setDeleteStatus] = useState({ message: '', success: false });
    const [closeProgress, setCloseProgress] = useState(0);
    const [isCleaningUp, setIsCleaningUp] = useState(false);
    const [cleanupResult, setCleanupResult] = useState(null);
    const [selectedCleanupImages, setSelectedCleanupImages] = useState([]);


    const cleanupUnreferencedImages = async (dryRun = true) => {
        try {
            // Reset selection when starting a new cleanup
            setSelectedCleanupImages([]);
            const response = await fetch(`${BASE_URL}/cleanup/${projectId}/images?dryRun=${dryRun}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.details || error.error || 'Failed to cleanup images');
            }

            const result = await response.json();
            console.log('Cleanup response:', result);

            // The API returns images with URLs already
            // Always return the API response as is
            return result;
        } catch (error) {
            console.error('Error cleaning up images:', error);
            throw error;
        }
    };

    const handleCleanup = async () => {
        setIsCleaningUp(true);
        setCleanupResult(null);
        try {
            const result = await cleanupUnreferencedImages(true);
            
            // Convert API response to our format
            const unreferencedImages = result.images?.map(img => ({
                path: img.key,
                name: img.name,
                imageUrl: img.url
            })) || [];

            if (unreferencedImages.length > 0) {
                // Show preview result
                const cleanupState = {
                    success: true,
                    message: `Found ${unreferencedImages.length} unreferenced images`,
                    details: unreferencedImages
                };
                console.log('Setting cleanup result state:', cleanupState);
                setCleanupResult(cleanupState);
            } else {
                setCleanupResult({
                    success: true,
                    message: 'No unreferenced images found',
                    details: []
                });
            }
        } catch (error) {
            console.error('Error during cleanup:', error);
            setCleanupResult({
                success: false,
                message: error.message || 'Error during cleanup',
                details: error.details || error.message
            });
        } finally {
            setIsCleaningUp(false);
        }
    };

    const handleClearCache = async () => {
        let storeName = `${projectId}-images`;
        setIsLoading(true);  // Start showing the progress bar
        setProgress(0);      // Reset the progress to 0
        await clearImageCache(storeName);
        await fetchProjectImages();  // Re-fetch images after clearing cache
    };

    const fetchProjectImages = async () => {
        try {

            console.log('Fetching project images with filter:', filter);
            const response = await fetch(`${BASE_URL}/project/${projectId}/images?filter=${filter}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            const data = await response.json();
            console.log('Project images response:', data);

            const totalImages = data.length;
            let loadedImages = 0;

            const fetchedImages = await Promise.all(
                data.map(async (image) => {
                    console.log('Loading image:', image);
                    let cachedImage = await getImage(projectId, image.name);
                    if (cachedImage) {
                        console.log('Found cached image for:', image.name);
                        loadedImages++;
                        setProgress(Math.round((loadedImages / totalImages) * 100));
                        return { ...image, imageUrl: cachedImage };
                    } else {
                        try {
                            console.log('Fetching image URL for:', image.name);
                            const response = await fetch(`${BASE_URL}/project/${projectId}/image/${image.name}`, {
                                headers: {
                                    'Authorization': `Bearer ${token}`,
                                },
                            });
                            const data = await response.json();
                            console.log('Image URL response for:', image.name, data);
                            await setImage(projectId, image.name, data.imageUrl);
                            loadedImages++;
                            setProgress(Math.round((loadedImages / totalImages) * 100));
                            return { ...image, imageUrl: data.imageUrl };
                        } catch (error) {
                            console.error('Error fetching image:', error, 'for image:', image);
                            return null;
                        }
                    }
                })
            );
            const filteredImages = fetchedImages.filter(image => image !== null);
            console.log('Setting images state:', filteredImages);
            setImages(filteredImages);
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching project images:', error);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchProjectImages();
    }, [projectId, token, filter]);

    const handleImageClick = (imageUrl) => {
        setSelectedImage(imageUrl);
        setIsModalOpen(true);
        // Add class to body when modal is open
        document.body.classList.add('modal-open');
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedImage(null);
        // Remove class from body when modal is closed
        document.body.classList.remove('modal-open');
    };

    const handleFilterChange = (e) => {
        setFilter(e.target.value);
        setSelectedImages([]); // Clear selection when changing filter
    };

    const handleImageSelect = (image) => {
        setSelectedImages(prev => {
            const isSelected = prev.some(img => img.name === image.name);
            if (isSelected) {
                return prev.filter(img => img.name !== image.name);
            } else {
                return [...prev, image];
            }
        });
    };

    const handleSelectAll = () => {
        const notUsedImages = images.filter(img => !img.isUsed);
        if (selectedImages.length === notUsedImages.length) {
            setSelectedImages([]);
        } else {
            setSelectedImages(notUsedImages);
        }
    };

    const handleDeleteConfirm = async (imagesToDelete) => {
        setIsDeleting(true);
        try {
            console.log('Deleting images:', imagesToDelete);
            const response = await fetch(`${BASE_URL}/delete/${projectId}/images`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    images: imagesToDelete.map(img => ({
                        name: img.name,
                        key: img.path
                    }))
                }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.details || error.error || 'Failed to delete images');
            }

            const data = await response.json();
            console.log('Delete response:', data);

            // Update project data if needed
            if (projectData?.project?.data) {
                const deletedImagePaths = imagesToDelete.map(img => img.path);
                setProjectData({
                    ...projectData,
                    project: {
                        ...projectData.project,
                        data: {
                            ...projectData.project.data,
                            images: projectData.project.data.images.filter(
                                img => !deletedImagePaths.includes(img.path)
                            )
                        }
                    }
                });
            }

            return data;
        } catch (error) {
            console.error('Error deleting images:', error);
            throw error;
        } finally {
            setIsDeleting(false);
        }
    };


    return (
        <div className="project-images">
            <h2>{projectName}'s Project Images</h2>
            {isLoading && (
                <div className="progress-bar">
                    <div className="progress-bar-fill" style={{ width: `${progress}%` }}></div>
                    <span>{progress}%</span>
                </div>
            )}

            <div className="filter-button-container">
                <div className="filter-dropdown">
                    <select onChange={handleFilterChange} value={filter}>
                        <option value="">All</option>
                        <option value="Not Used">Not Used</option>
                        <option value="Used">Used</option>
                        <option value="Choice">Choices</option>
                        <option value="ThisThen">ThisThen</option>
                        <option value="Plan">Schedules</option>
                        <option value="Social Story">Social Stories</option>
                    </select>
                </div>
                <div className="clear-cache-button">
                    <button onClick={handleClearCache}>Clear & Re-download</button>
                </div>
                <div className="cleanup-button">
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleCleanup}
                        disabled={isCleaningUp}
                        startIcon={isCleaningUp ? <CircularProgress size={20} /> : null}
                    >
                        {isCleaningUp ? 'Cleaning...' : 'Cleanup Unused Images'}
                    </Button>
                </div>

                {filter === 'Not Used' && (
                    <div className="selection-controls">
                        <Button 
                            variant="outlined"
                            onClick={handleSelectAll}
                        >
                            {selectedImages.length === images.filter(img => !img.isUsed).length ? 'Deselect All' : 'Select All'}
                        </Button>
                        {selectedImages.length > 0 && (
                            <Button 
                                variant="contained" 
                                color="error"
                                disabled={isDeleting}
                                startIcon={isDeleting ? <CircularProgress size={20} /> : null}
                                onClick={async () => {
                                    setIsDeleting(true);
                                    try {
                                        const response = await fetch(`${BASE_URL}/delete/${projectId}/images`, {
                                            method: 'DELETE',
                                            headers: {
                                                'Authorization': `Bearer ${token}`,
                                                'Content-Type': 'application/json'
                                            },
                                            body: JSON.stringify({
                                                images: selectedImages.map(img => ({
                                                    name: img.name,
                                                    key: img.path || `${projectId}/${img.name}`
                                                }))
                                            })
                                        });

                                        if (!response.ok) {
                                            const error = await response.json();
                                            throw new Error(error.details || error.error || 'Failed to delete images');
                                        }

                                        const data = await response.json();
                                        console.log('Delete response:', data);

                                        // Update local state
                                        setImages(prev => prev.filter(img => !selectedImages.some(selected => selected.name === img.name)));
                                        setSelectedImages([]);

                                        // Update project data if needed
                                        if (projectData?.project?.data) {
                                            const deletedImageIds = selectedImages.map(img => img._id);
                                            setProjectData({
                                                ...projectData,
                                                project: {
                                                    ...projectData.project,
                                                    data: {
                                                        ...projectData.project.data,
                                                        images: projectData.project.data.images.filter(
                                                            img => !deletedImageIds.includes(img._id)
                                                        )
                                                    }
                                                }
                                            });
                                        }
                                    } catch (error) {
                                        console.error('Error deleting images:', error);
                                        alert('Error: ' + error.message);
                                    } finally {
                                        setIsDeleting(false);
                                    }
                                }}
                            >
                                {isDeleting ? 'Deleting...' : `Delete Selected (${selectedImages.length})`}
                            </Button>
                        )}
                    </div>
                )}
            </div>
            <div className="images-grid">
                {images.map((image, index) => (
                    <div key={index} className="image-container">
                        {filter === 'Not Used' && !image.isUsed && (
                            <Checkbox
                                checked={selectedImages.some(img => img.name === image.name)}
                                onChange={() => handleImageSelect(image)}
                                className="image-checkbox"
                            />
                        )}
                        <img
                            src={image.imageUrl}
                            alt={image.name}
                            onClick={() => handleImageClick(image.imageUrl)}
                            className="project-image"
                        />
                        <div className="image-metadata">
                            <p>{image.name}</p>
                            {image.contexts && image.contexts.length > 0 && (
                                <ul>
                                    {image.contexts.map((context, idx) => (
                                        <li key={idx}>{context}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <ImagePreviewModal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                imageUrl={selectedImage}
            />

            <Dialog
                open={!!cleanupResult}
                onClose={() => !isCleaningUp && setCleanupResult(null)}
                maxWidth="md"
                fullWidth
                disableEscapeKeyDown={isCleaningUp}
            >
                <DialogTitle>
                    Cleanup Unreferenced Images
                    <IconButton
                        aria-label="close"
                        onClick={() => !isCleaningUp && setCleanupResult(null)}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                        disabled={isCleaningUp}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {isCleaningUp ? (
                        <Box sx={{ textAlign: 'center', padding: '2rem' }}>
                            <CircularProgress size={24} />
                            <Typography variant="body1" sx={{ marginTop: '1rem' }}>
                                Processing images...
                            </Typography>
                        </Box>
                    ) : cleanupResult?.success && !cleanupResult.details?.length ? (
                        <Box sx={{ textAlign: 'center', padding: '2rem' }}>
                            <Typography variant="body1" color="success.main" sx={{ marginTop: '1rem' }}>
                                {cleanupResult.message}
                            </Typography>
                        </Box>
                    ) : cleanupResult?.details?.length > 0 ? (
                        <>
                            <Typography variant="body1" sx={{ marginBottom: '1rem' }}>
                                Found {cleanupResult.details.length} unreferenced images. Select the images you want to keep and relink to the project.
                                Unselected images will be deleted.
                            </Typography>
                            <Box sx={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        if (selectedCleanupImages.length === cleanupResult.details.length) {
                                            setSelectedCleanupImages([]);
                                        } else {
                                            setSelectedCleanupImages(cleanupResult.details);
                                        }
                                    }}
                                >
                                    {selectedCleanupImages.length === cleanupResult.details.length ? 'Deselect All' : 'Select All'}
                                </Button>
                                <Typography variant="body2">
                                    {selectedCleanupImages.length} of {cleanupResult.details.length} selected
                                </Typography>
                            </Box>
                            <Grid container spacing={2} sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                {cleanupResult.details.map((file, index) => {
                                    const fileName = file.name || (typeof file === 'string' ? file.split('/').pop() : '');
                                    const isSelected = selectedCleanupImages.some(img => img.path === (file.path || file));
                                    return (
                                        <Grid item xs={12} sm={6} md={4} key={index}>
                                            <Box
                                                sx={{
                                                    position: 'relative',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    padding: '8px',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                    }
                                                }}
                                                onClick={() => {
                                                    if (isSelected) {
                                                        setSelectedCleanupImages(prev => 
                                                            prev.filter(img => img.path !== file.path)
                                                        );
                                                    } else {
                                                        setSelectedCleanupImages(prev => [...prev, file]);
                                                    }
                                                }}
                                            >
                                                <Checkbox
                                                    checked={isSelected}
                                                    sx={{ position: 'absolute', top: 4, right: 4 }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={() => {
                                                        if (isSelected) {
                                                            setSelectedCleanupImages(prev => 
                                                                prev.filter(img => img.path !== file.path)
                                                            );
                                                        } else {
                                                            setSelectedCleanupImages(prev => [...prev, file]);
                                                        }
                                                    }}
                                                />
                                                <Box
                                                    component="img"
                                                    src={file.imageUrl}
                                                    alt={fileName}
                                                    sx={{
                                                        width: '100%',
                                                        height: '150px',
                                                        objectFit: 'contain'
                                                    }}
                                                />
                                                <Typography variant="body2" sx={{ marginTop: '8px', wordBreak: 'break-word' }}>
                                                    {fileName}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </>
                    ) : cleanupResult?.success === false ? (
                        <Box sx={{ textAlign: 'center', padding: '2rem' }}>
                            <Typography variant="body1" color="error.main" sx={{ marginTop: '1rem' }}>
                                {cleanupResult.message}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography variant="body1" sx={{ textAlign: 'center', padding: '2rem' }}>
                            {cleanupResult?.message || 'No unreferenced images found'}
                        </Typography>
                    )}
                </DialogContent>
                {cleanupResult?.details?.length > 0 && (
                    <DialogActions>
                        <Button 
                            onClick={() => setCleanupResult(null)}
                            disabled={isCleaningUp}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={async () => {
                                setIsCleaningUp(true);
                                try {
                                    // Relink selected images
                                    if (selectedCleanupImages.length > 0) {
                                        const response = await fetch(`${BASE_URL}/restore/${projectId}/images`, {
                                            method: 'POST',
                                            headers: {
                                                'Authorization': `Bearer ${token}`,
                                                'Content-Type': 'application/json'
                                            },
                                            body: JSON.stringify({
                                                images: selectedCleanupImages.map(img => img.path || img)
                                            })
                                        });

                                        if (!response.ok) {
                                            const error = await response.json();
                                            throw new Error(error.details || error.error || 'Failed to restore images');
                                        }

                                        const data = await response.json();

                                        // Update project data with restored images
                                        if (projectData?.project?.data) {
                                            setProjectData({
                                                ...projectData,
                                                project: {
                                                    ...projectData.project,
                                                    data: {
                                                        ...projectData.project.data,
                                                        images: [
                                                            ...projectData.project.data.images,
                                                            ...data.restoredImages
                                                        ]
                                                    }
                                                }
                                            });
                                        }
                                    }

                                    // Delete unselected images
                                    const imagesToDelete = (cleanupResult.details || []).filter(
                                        file => !selectedCleanupImages.some(img => img.path === file.path)
                                    );
                                    console.log('Images to delete:', imagesToDelete);
                                    
                                    if (imagesToDelete.length > 0) {
                                        try {
                                            const deleteResult = await handleDeleteConfirm(imagesToDelete);
                                            console.log('Delete completed with result:', deleteResult);

                                            // No need to refresh images as we're only cleaning up unreferenced ones

                                            // Close dialog and show success message
                                            setCleanupResult({
                                                success: true,
                                                message: deleteResult.message || 'Images deleted successfully',
                                                details: []
                                            });

                                            // Wait a moment to show success message then close
                                            setTimeout(() => {
                                                setCleanupResult(null);
                                                setSelectedCleanupImages([]);
                                                setIsCleaningUp(false);
                                            }, 1500);
                                        } catch (error) {
                                            console.error('Error during cleanup:', error);
                                            setCleanupResult({
                                                success: false,
                                                message: error.message || 'Error during cleanup',
                                                details: []
                                            });
                                            setIsCleaningUp(false);
                                        }
                                    } else {
                                        // Just close dialog if no images to delete
                                        setCleanupResult(null);
                                        setSelectedCleanupImages([]);
                                        setIsCleaningUp(false);
                                    }
                                } catch (error) {
                                    console.error('Error during cleanup:', error);
                                    setCleanupResult({
                                        success: false,
                                        message: error.message || 'Error during cleanup',
                                        details: []
                                    });
                                    setIsCleaningUp(false);
                                }
                            }}
                            disabled={isCleaningUp}
                            startIcon={isCleaningUp ? <CircularProgress size={20} /> : null}
                        >
                            {isCleaningUp ? 'Processing...' : 'Confirm Cleanup'}
                        </Button>
                    </DialogActions>
                )}
            </Dialog>


        </div>
    );
};

export default ProjectImages;